/**
 * Builds the project list the `/new` composer offers.
 *
 * Extracted from `useDraftProjectChoice` so the decision about WHICH projects
 * a draft may target is testable without mounting the hook.
 */
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { Project } from "../../types";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import {
  deriveCloudProjectKey,
  resolveCloudProjectEnvironmentId,
  toRegisteringCatalogRow,
} from "../sidebar/cloudProjectsGroup.logic";
import {
  canStartWorkInProject,
  catalogProvesProjectUnregistered,
  isPickableProject,
} from "../projects/pickableProject";
import { resolveVisibleCloudRows } from "../projects/visibleCloudRows";
import { deriveProjectDisplayName, type ProjectSummary } from "./NewThreadLanding.logic";
import {
  buildPickerProjectSummaries,
  buildUnconnectedCloudProjectSummary,
} from "./pickerProjectSummaries";

export interface DraftPickerProjectSummariesInput {
  readonly projects: readonly Project[];
  readonly cloudCatalogRows: readonly CloudProjectCatalogRow[];
  /**
   * Whether the catalog answered cleanly. False means "we could not ask", which
   * must not read as "the project is gone". See `isCloudRegistrationLive`.
   */
  readonly cloudCatalogAuthoritative: boolean;
  /** Canonical archived boards, which no picker offers. */
  readonly archivedCmmdProjectIds: ReadonlySet<number>;
  readonly desktopInventoryProjects: Parameters<
    typeof buildPickerProjectSummaries
  >[0]["desktopInventoryProjects"];
  readonly resolveProjectDisplayName: (
    environmentId: string,
    projectId: string,
  ) => string | null | undefined;
}

/**
 * Whether a draft may target this project.
 *
 * Two questions, both shared with the sidebar picker so the two lists cannot
 * drift: may a picker offer it at all, and can a turn actually start there.
 * See components/projects/pickableProject.ts for why they are separate.
 */
function isDraftTargetableProject(
  project: Project,
  context: {
    readonly cloudCatalogRows: readonly CloudProjectCatalogRow[];
    readonly cloudCatalogAuthoritative: boolean;
    readonly archivedCmmdProjectIds: ReadonlySet<number>;
  },
): boolean {
  if (!canStartWorkInProject(project)) return false;
  const runtimeHosted = classifyEnvironmentId(project.environmentId).isCloud;
  const provenUnregistered =
    runtimeHosted &&
    catalogProvesProjectUnregistered(context.cloudCatalogRows.map(toRegisteringCatalogRow), {
      environmentId: project.environmentId,
      projectId: project.id,
      cwd: project.cwd,
    });
  return isPickableProject({
    runtimeHosted,
    catalogAuthoritative: context.cloudCatalogAuthoritative,
    provenUnregistered,
    cmmdProjectId: resolveProjectCmmdId(project, context.cloudCatalogRows),
    archivedCmmdProjectIds: context.archivedCmmdProjectIds,
  });
}

/**
 * The CMMD board behind a project, when the catalog can name one.
 *
 * Read off the catalog rather than the project, because a connected checkout
 * carries no board id of its own.
 */
function resolveProjectCmmdId(
  project: Project,
  rows: readonly CloudProjectCatalogRow[],
): number | null {
  const row = rows.find(
    (candidate) =>
      resolveCloudProjectEnvironmentId(candidate) === project.environmentId &&
      (candidate.resolvedRef?.projectId === project.id ||
        (project.cwd.length > 0 && candidate.workspaceRoot === project.cwd)),
  );
  return row?.cmmdProjectId ?? null;
}

export function buildDraftPickerProjectSummaries(
  input: DraftPickerProjectSummariesInput,
): ProjectSummary[] {
  // The one rule for which catalog rows are still worth offering, shared with
  // the sidebar (components/projects/visibleCloudRows.ts): archived boards
  // drop out, and a row a resident project already names is not offered a
  // second time as pre-connect — decided by Registration identity, so a
  // sibling Registration on the same per-owner runtime stays offered.
  const { preConnectCloudRows } = resolveVisibleCloudRows({
    catalogRows: input.cloudCatalogRows,
    catalogMembershipIsAuthoritative: input.cloudCatalogAuthoritative,
    archivedCmmdProjectIds: input.archivedCmmdProjectIds,
    representedProjectIdentities: input.projects.map((project) => ({
      environmentId: project.environmentId,
      projectId: project.id,
      cwd: project.cwd,
    })),
    suppressingProjectImportJobs: [],
    surface: "draft-picker",
  });

  const connectedSummaries = buildPickerProjectSummaries({
    connectedProjects: input.projects
      .filter((project) =>
        isDraftTargetableProject(project, {
          cloudCatalogRows: input.cloudCatalogRows,
          cloudCatalogAuthoritative: input.cloudCatalogAuthoritative,
          archivedCmmdProjectIds: input.archivedCmmdProjectIds,
        }),
      )
      .map((project) => ({
        id: project.id,
        environmentId: project.environmentId,
        // A checkout's own name, for anything imported from CMMD, is a slug of
        // the board title — prefer the name chosen in Forge when one is linked,
        // and only fall back to the checkout otherwise.
        name:
          input.resolveProjectDisplayName(project.environmentId, project.id) ??
          deriveProjectDisplayName(project),
        cwd: project.cwd,
      })),
    // Cloud rows are handled below, straight from `preConnectCloudRows`: this
    // loop's own de-dup (`pickerProjectSummaries.ts`'s ledger) assumes an
    // UNFILTERED catalog and reasons about ambiguity itself. Fed rows that
    // Registration identity has already cleared, that reasoning misfires — see
    // `buildUnconnectedCloudProjectSummary`'s doc comment.
    cloudCatalogRows: [],
    desktopInventoryProjects: [],
  });

  const cloudSummaries = preConnectCloudRows.map((row) =>
    buildUnconnectedCloudProjectSummary({
      projectKey: deriveCloudProjectKey(row),
      environmentId: resolveCloudProjectEnvironmentId(row),
      projectName: row.projectName,
      workspaceRoot: row.workspaceRoot,
      resolvedProjectId: row.resolvedRef?.projectId ?? null,
      icon: row.icon,
      iconUrl: row.iconUrl,
    }),
  );

  const desktopSummaries = buildPickerProjectSummaries({
    connectedProjects: [],
    cloudCatalogRows: [],
    desktopInventoryProjects: input.desktopInventoryProjects,
  });

  return [...connectedSummaries, ...cloudSummaries, ...desktopSummaries];
}
